/**
 * A domain is one audience of one product: the backend deploys an agent per domain, scopes its
 * knowledge to it, and decides from it which credential it will accept.
 *
 * The list is not a property of this widget. Every product has its own, and the values travel to
 * the backend exactly as the host wrote them, so a name this library invented on its own could
 * only ever be refused. The host therefore declares the domains it serves, and their rules, in
 * `ChatbotConfig.domains`. There is deliberately no default and no fallback: a domain nobody
 * declared is an error, never an anonymous visitor.
 */
export type Domain = string;

/**
 * How a domain's caller proves who it is. Exactly one mechanism per domain, never both: modelled
 * as one value rather than two booleans so a configuration cannot claim both at once.
 *
 * `bearer`: the host holds a session token and hands it over. The backend reads the caller's
 * identity out of it.
 *
 * `anonymous`: nobody issues this domain a credential, so its identity is a UUID the visitor
 * mints in its own browser storage and sends in a header. This is the only domain whose identity
 * the backend cannot verify, and it is the backend that decides which one it is, not the surface
 * the widget happens to run on.
 */
export type DomainIdentity = "bearer" | "anonymous";

/** Everything the widget needs to know about one domain, as its host declares it. */
export interface ChatbotDomainRules {
    /** The backend agent that answers for this domain. */
    agent: string;

    /** How this domain's caller identifies itself. */
    identity: DomainIdentity;

    /**
     * Whether this domain's users may rename and delete their own conversations.
     *
     * A domain whose identity cannot be verified must have this off: the backend refuses those
     * writes from an identity that is a UUID the caller minted itself, so offering the control
     * would only promise something the server will not do. A host can still switch it off for a
     * domain that would otherwise allow it, through the sidebar's own input.
     */
    allowsSessionManagement: boolean;

    /**
     * Whether this domain may offer the dropdown that switches agent mid conversation.
     *
     * True only where switching makes sense and where the credential can read the protected
     * listing the dropdown is built from. The component's own input is a second switch on top of
     * this one, so a host can hide the dropdown on a domain that allows it.
     */
    showsAgentPicker: boolean;

    /**
     * How a tester signs in as one of this domain's users, to try an agent out the way they would
     * meet it. Omitted for a domain nobody can sign into, which is then simply never asked for.
     *
     * Each domain's credentials are issued and verified under a key of its own, so there is no
     * way to reach this by relabelling the credential already in hand: a real sign in is the only
     * way.
     */
    login?: ChatbotDomainLogin;
}

/**
 * The endpoint that turns a username and a password into one domain's session token.
 *
 * Deliberately an API and not a page. A page would mean opening the domain's own web application
 * in a window, which only exists for some domains: the ones served by a native client have no
 * such page, and this same exchange has to be reproducible there with a dialog of its own. Asking
 * the backend directly is the one shape that works everywhere.
 *
 * The endpoint receives `{ username, password, ...extraFields }` as JSON, with the **password
 * base64 encoded**. That is the contract, the same for every domain, so that nothing here has to
 * know which endpoint wants which shape. Base64 is an encoding and not a protection: what keeps
 * the password safe on the way is TLS, which is why a sign in over plain HTTP is refused unless
 * the build has declared itself local.
 */
export interface ChatbotDomainLogin {
    /** Absolute URL of the endpoint. It is the host that knows it, never this library. */
    url: string;

    /**
     * Anything this endpoint needs beyond a username and a password, asked for in the same
     * dialog, in this order. Values are sent as typed, under the name each one declares.
     */
    extraFields?: ChatbotLoginField[];

    /**
     * Field of the JSON answer that holds the session token. Defaults to `sessionToken`, which is
     * what most endpoints in this ecosystem answer with; a host whose endpoint names it
     * differently says so here rather than adapting the endpoint.
     */
    tokenField?: string;
}

/** The domains a host declares, keyed by the name that travels to the backend. */
export type ChatbotDomains = Readonly<Record<Domain, ChatbotDomainRules>>;

/**
 * The one header every request carries, naming the surface the caller is.
 *
 * It selects the domain and nothing else: which credential the backend checks, which agent
 * answers, which conversation history is reachable. It deliberately carries no account
 * identifier. For every domain with a credential the caller's identity is already inside the
 * bearer token, and a second copy of it in a header would be an unverified answer to a question
 * the token has already answered, readable by every proxy and log on the way.
 */
export const CLIENT_DOMAIN_HEADER = "X-Client-Domain";

/**
 * The domain without a credential is the exception: nobody issues it one, so its identity has to
 * travel on its own. It alternates between these two, the first until the visitor converts into
 * a lead and the second afterwards. Both carry a UUID, hence the suffix.
 */
export const ANONYMOUS_UUID_HEADER = "X-Anonymous-Uuid";
export const CUSTOMER_UUID_HEADER = "X-Customer-Uuid";

/**
 * The credential that authorises testing an undeployed configuration while identifying as
 * another domain's user.
 *
 * Two credentials, two separate questions. `Authorization` says who the agent is answering, and
 * this one says who asked for a test. Both are verified: the backend allows an undeployed
 * configuration to run only for a caller that proves it may ask for one, which is why the
 * tester's own credential has to travel even though it is not the identity of the conversation.
 */
export const TEST_AUTHORIZATION_HEADER = "X-Draft-Test-Authorization";

/**
 * The rules for one domain, or an error naming what the host got wrong.
 *
 * Throwing is the point. The previous behaviour, quietly resolving anything unknown to the
 * anonymous domain, turned a host's configuration mistake into requests that identified as a
 * visitor nobody had declared, and the symptom surfaced much later as an answer scoped to the
 * wrong audience.
 */
export function domainRules(domains: ChatbotDomains, domain: Domain | null | undefined): ChatbotDomainRules {
    if (!domain) {
        throw new Error("Chatbot: no domain was declared. A host must declare one, through the component's input or the query parameter.");
    }

    // `hasOwnProperty`, not a plain lookup: every object inherits `toString` and friends, and a
    // domain called one of those would otherwise resolve to a function off the prototype and be
    // taken for a declaration.
    const rules = isDeclaredDomain(domains, domain) ? domains[domain] : undefined;
    if (!rules) {
        throw new Error(`Chatbot: domain "${domain}" is not declared in the injected configuration. Declared: ${Object.keys(domains).join(", ") || "none"}.`);
    }

    return rules;
}

/** Whether this domain is one the host declared, without throwing on the ones that are not. */
export function isDeclaredDomain(domains: ChatbotDomains, domain: Domain | null | undefined): boolean {
    return !!domain && Object.prototype.hasOwnProperty.call(domains, domain);
}

/**
 * Rejects a domain declaration that is incomplete or self contradictory, naming the domain and
 * the field. Called while the host's providers are being built, so a mistake stops the
 * application at startup rather than at the first request.
 */
export function assertValidDomainRules(domain: Domain, rules: ChatbotDomainRules | undefined): void {
    if (!rules || typeof rules !== "object") {
        throw domainError(domain, "has no rules object");
    }
    if (typeof rules.agent !== "string" || rules.agent.trim() === "") {
        throw domainError(domain, "declares no agent");
    }
    if (rules.identity !== "bearer" && rules.identity !== "anonymous") {
        throw domainError(domain, 'must declare identity as "bearer" or "anonymous"');
    }
    if (typeof rules.allowsSessionManagement !== "boolean") {
        throw domainError(domain, "must declare allowsSessionManagement");
    }
    if (typeof rules.showsAgentPicker !== "boolean") {
        throw domainError(domain, "must declare showsAgentPicker");
    }

    if (rules.identity === "anonymous" && rules.allowsSessionManagement) {
        throw domainError(domain, "cannot allow session management: the backend refuses those writes from an identity it cannot verify");
    }

    const login = rules.login;
    if (login === undefined) {
        return;
    }
    if (rules.identity !== "bearer") {
        throw domainError(domain, "declares a sign in but needs no credential");
    }
    if (typeof login.url !== "string" || login.url.trim() === "") {
        throw domainError(domain, "declares a sign in with no URL");
    }
    try {
        new URL(login.url);
    } catch {
        throw domainError(domain, "declares a sign in URL that is not absolute");
    }
    if (login.extraFields !== undefined) {
        if (!Array.isArray(login.extraFields) || login.extraFields.some((field) => !field || typeof field.name !== "string" || field.name.trim() === "")) {
            throw domainError(domain, "declares sign in fields that are not names");
        }
        if (login.extraFields.some((field) => field.default !== undefined && typeof field.default !== "string")) {
            throw domainError(domain, "declares a sign in field whose offered value is not text");
        }
    }
    if (login.tokenField !== undefined && (typeof login.tokenField !== "string" || login.tokenField.trim() === "")) {
        throw domainError(domain, "declares a token field that is not a name");
    }
}

/**
 * The domain that must be signed into before this agent may be asked anything, or null when the
 * identity already in use is the right one.
 *
 * An agent that answers for a declared domain may only be talked to **as one of that domain's
 * users**: its answers are scoped to who is asking, so a conversation held as somebody else is
 * not a test of that agent, it is a different conversation that happens to reach the same
 * runner. An agent no declared domain claims (a shared or subordinate one) keeps whichever
 * identity is in use.
 */
export function domainToSignInto(domains: ChatbotDomains, agent: string | null | undefined, activeDomain: Domain | null): Domain | null {
    const target = domainForAgent(domains, agent);
    if (!target || target.name === activeDomain) {
        return null;
    }
    return target.name;
}

/** One value a domain's login endpoint needs beyond the username and the password. */
export interface ChatbotLoginField {
    /** The name it is sent under, which is what that endpoint calls it. */
    name: string;

    /**
     * Offered in the dialog, already filled in, for a value that is almost always the same one.
     * The tester overwrites it when they need another.
     */
    default?: string;
}

/**
 * The declared domain an agent answers for, or null when no declared domain claims it.
 *
 * This is what makes signing in generic: the tester picks an agent, and which identity that
 * conversation needs follows from the host's own declaration rather than from a list of domain
 * names the tester has to know. An agent nobody claims (a shared or subordinate one) keeps the
 * identity already in use.
 */
export function domainForAgent(domains: ChatbotDomains, agent: string | null | undefined): { name: Domain; rules: ChatbotDomainRules } | null {
    if (!agent) {
        return null;
    }
    for (const [name, rules] of Object.entries(domains)) {
        if (rules.agent === agent) {
            return { name, rules };
        }
    }
    return null;
}

function domainError(domain: Domain, message: string): Error {
    return new Error(`Chatbot: domain "${domain}" ${message}.`);
}
